/* global mapboxgl */
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
/**
 * @module LocationsListing
 */
import React from 'react';
import {
  callGtagEvent,
  callSegmentTrack,
} from '@lifechurch/web-tools-io/dist/utils/helpers/analytics';
import useAuth from '@lifechurch/web-tools-io/dist/hooks/useAuth';
import useWindowSize from '@lifechurch/web-tools-io/dist/hooks/useWindowSize';
import {
  CENTER_OF_USA,
  Log,
} from '@lifechurch/web-tools-io/dist/utils/helpers';
import { setMapCoordinates } from '@lifechurch/web-tools-io/dist/utils/helpers/locationsHelper';
import { getMagnoliaItem } from '@lifechurch/web-tools-io/dist/utils/helpers/magnolia/getMagnoliaItem';
import { getQueryParams } from '@lifechurch/web-tools-io/dist/utils/helpers/queryParams';
import { getLetterForNumber } from '@lifechurch/web-tools-io/dist/utils/helpers/strings';
import { implementUtmParams } from '@lifechurch/web-tools-io/dist/utils/helpers/utmParams';
import { convertValueToClassName } from '@lifechurch/web-tools-io/dist/utils/helpers/validators';
import _, { flattenDeep, split } from 'lodash';
import Mapbox from './_Mapbox';
import { metroCoordinates } from './metro';
import {
  ACTIONS,
  BREAKPOINTS,
  ENDPOINT_WORKSPACE_MAP,
  ENVIRONMENT,
  EVENTS,
  HIDDEN_CAMPUSES,
  MGNL_ENV_VARS,
} from '../../helpers/constants';
import './LocationsListing.scss';

const defaultZoomOut = 3.4;
const defaultZoomIn = 10;

const ListItem = (props) => {
  const { user } = useAuth();
  const { lc, link, onFlyTo, onStateClicked, onZoomOut, override, state } =
    props;
  const [open, setOpen] = React.useState(false);

  /**
   * Handler function for location state click event.
   *
   * Note: The ignore directive is added as it's not needed for test coverage.
   * Everything fires off properly and is asserted against since the lat/lng
   * coordinates should *always* fall in their acceptable ranges of longitude
   * between -180 and 180, and latitude between -90 and 90.
   */
  const onLocationState = () => {
    if (mapboxgl && window?.lifeChurchMap) {
      const bounds = new mapboxgl.LngLatBounds();

      lc.forEach(({ latitude, longitude }) => {
        /* istanbul ignore next */
        if (
          Number(longitude) >= -180 &&
          Number(longitude) <= 180 &&
          Number(latitude) <= 90 &&
          Number(latitude) >= -90
        ) {
          bounds.extend([Number(longitude), Number(latitude)]);
        }
      });

      window.scrollTo({ behavior: 'smooth', top: 0 });
      window.lifeChurchMap.fitBounds(bounds, {
        maxZoom: defaultZoomIn,
        padding: 80,
      });
    }
  };

  const handleZoomOut = () => {
    const { lat, lng } = CENTER_OF_USA;
    onFlyTo([lng, lat], defaultZoomOut);
    onZoomOut(defaultZoomOut);
  };

  /**
   * Handler function to trigger analytics tracking.
   *
   * @param {Event} event - The Event object associated with the click.
   * @param {string} label - The label value used in the analytics properties data.
   */
  const callAnalytics = (event, label) => {
    /* istanbul ignore next */
    if (event?.target?.preventDefault && ENVIRONMENT.test) {
      event.preventDefault();
    }
    callSegmentTrack({
      event: EVENTS.buttonAction,
      properties: {
        action: ACTIONS.clicked,
        component: 'Locations Map',
        component_url: event?.currentTarget?.getAttribute('href'),
        label: label || event?.currentTarget?.textContent,
        logged_in: !!user,
        preferred_campus: null, // User preferred campus not presently available without specific call to API to get user-specific data (such as with Web Giving).
        referrer: document?.referrer || null,
        title: document?.title || '',
        url: window?.location?.href,
        user_id: user?.['https://www.life.church/rock_person_alias_id'],
      },
    });

    callGtagEvent({
      parameters: {
        event_category: 'Map',
        event_label: label,
      },
      type: 'click',
    });
  };

  /**
   * Handler function for State click.
   *
   * @param {Event} event - The Event object associated with the click.
   * @param {boolean} isLocationClick - Boolean flag denoting whether or not it is a location-specific click target.
   */
  const handleStateClicked = (event, isLocationClick) => {
    if (isLocationClick) {
      onLocationState();
    } else {
      handleZoomOut();
    }
    setOpen(!open);
    onStateClicked(isLocationClick ? state : '');
    callAnalytics(event, isLocationClick ? state : 'View all states');
  };

  React.useEffect(() => {
    const autoLoadMap = () => {
      const openParam = getQueryParams('open');
      if (
        typeof openParam === 'string' &&
        state.replace(/-/g, '').toLowerCase() === openParam.toLowerCase()
      ) {
        handleStateClicked(null, true);
      }
    };

    document.addEventListener('mapLoaded', autoLoadMap);
    return () => {
      document.removeEventListener('mapLoaded', autoLoadMap);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`locations-item ${open && 'active'}`}
      data-testid="lc-locations-item"
    >
      <button
        className="locations-state"
        onClick={(event) => handleStateClicked(event, true)}
      >
        {state.replace(/-/g, ' ')}
      </button>
      <ul className="sub-locations">
        <li>
          <button
            className="view-all-state"
            onClick={(event) => handleStateClicked(event, false)}
          >
            <svg
              aria-hidden="true"
              data-icon="arrow-circle-left"
              data-prefix="fal"
              focusable="false"
              height="19px"
              viewBox="0 0 512 512"
              width="19px"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M504 256C504 119 393 8 256 8S8 119 8 256s111 248 248 248 248-111 248-248zM256 472c-118.7 0-216-96.1-216-216 0-118.7 96.1-216 216-216 118.7 0 216 96.1 216 216 0 118.7-96.1 216-216 216zm-12.5-92.5l-115.1-115c-4.7-4.7-4.7-12.3 0-17l115.1-115c4.7-4.7 12.3-4.7 17 0l6.9 6.9c4.7 4.7 4.7 12.5-.2 17.1L181.7 239H372c6.6 0 12 5.4 12 12v10c0 6.6-5.4 12-12 12H181.7l85.6 82.5c4.8 4.7 4.9 12.4.2 17.1l-6.9 6.9c-4.8 4.7-12.4 4.7-17.1 0z"
                fill="currentColor"
              ></path>
            </svg>
            <span>View all states</span>
          </button>
        </li>
        <li>
          <button
            className="locations-state state-title"
            onClick={(event) => handleStateClicked(event, false)}
          >
            {state.replace(/-/g, ' ')}
          </button>
        </li>
        {lc.map((element, i) => {
          let overrideItem = null;
          if (override?.['@nodes']?.length) {
            override['@nodes'].forEach((item) => {
              if (
                !!override[item].locationName &&
                !!element.name &&
                override[item].locationName.toLowerCase().trim() ===
                  element.name.toLowerCase().trim()
              ) {
                overrideItem = override[item];
              }
            });
          }

          return (
            <li key={element['@id']}>
              <a
                className="locations-list-item"
                href={implementUtmParams(
                  overrideItem ? overrideItem.redirectURL : `/${element.slug}/`,
                  window?.location,
                )}
                onClick={(event) => {
                  callAnalytics(
                    event,
                    `Campus ${element.campusCode.toUpperCase()}`,
                  );
                }}
              >
                <div
                  className={`location-icon${
                    !!element.phase && element.phase.field === 'Pre-Launch'
                      ? ' coming-soon'
                      : ''
                  }`}
                >
                  {getLetterForNumber(i)}
                </div>
                <div className="location-detail">
                  <h6 className="title">{element.name}</h6>
                  <p className="address">
                    {!!element.primaryAddress &&
                      `${
                        element.primaryAddress.street1
                          ? `${element.primaryAddress.street1},${'\n'}`
                          : ''
                      }` +
                        `${
                          element.primaryAddress.street2
                            ? `${element.primaryAddress.street2},${'\n'}`
                            : ''
                        }` +
                        `${
                          element.primaryAddress.cityOrTown
                            ? `${element.primaryAddress.cityOrTown}, `
                            : ''
                        }` +
                        `${
                          element.primaryAddress.state
                            ? `${element.primaryAddress.state}`
                            : ''
                        }`}
                  </p>
                </div>
              </a>
            </li>
          );
        })}
        <li>
          <a
            className="location-link display-block p-normal text-center"
            data-testid="lc-location-link-label"
            href={implementUtmParams(link.url, window?.location)}
            onClick={(event) => {
              callAnalytics(event);
            }}
            target={link.target}
          >
            {link.linklabel}
          </a>
        </li>
      </ul>
    </div>
  );
};

const LocationsListing = (props) => {
  const {
    churchOnline,
    headingText,
    linklabel,
    override,
    sbOnMobile,
    sbOnTabletAndUp,
    sectionId,
    target,
    url,
  } = props;
  const { user } = useAuth();
  const { isMobile } = useWindowSize();

  const [locations, setLocations] = React.useState(null);
  const [isLive, setIsLive] = React.useState(null);
  const [center, setCenter] = React.useState(null);
  const [zoom, setZoom] = React.useState(defaultZoomOut);
  const [stateClicked, setStateClicked] = React.useState('');

  const link = {
    linklabel,
    target,
    url,
  };

  function loadMapWithMetroCode() {
    const metro = getQueryParams('metro');
    /* istanbul ignore next */
    if (metro) {
      const metroDetails = metroCoordinates.find((item) => {
        return item.name === metro;
      });
      if (metroDetails) {
        const { lat, lng, mobileZoom, zoom: metroDetailsZoom } = metroDetails;
        const coordinates = { lat, lng };
        const metroZoom = isMobile ? mobileZoom : metroDetailsZoom;
        setMapCoordinates(coordinates, metroZoom, { setCenter, setZoom });
      }
    }
  }
  /* istanbul ignore next */
  const setLatLng = (loc) =>
    loc.forEach((item) => {
      if (!item.latitude) {
        item.latitude = item?.primaryAddress?.latitude ?? '';
      }
      if (!item.longitude) {
        item.longitude = item?.primaryAddress?.longitude ?? '';
      }
    });

  React.useEffect(() => {
    loadMapWithMetroCode();
    /* istanbul ignore next */
    const setLive = setInterval(() => {
      if (typeof window.__isLive !== 'undefined') {
        setIsLive(window.__isLive);
        clearInterval(setLive);
      }
    }, 500);

    async function fetchLocations() {
      try {
        const response = await getMagnoliaItem({
          caller:
            'src/components/LocationsListing/LocationsListing.js > fetchLocations',
          forceCall: props?.testOverride,
          mgnlEnvVars: MGNL_ENV_VARS,
          path: encodeURI(`/.rest/delivery/location?public=true&orderBy=name`),
          workspaceMap: ENDPOINT_WORKSPACE_MAP,
        }); // NOSONAR
        let locationsJson =
          response?.results.filter(
            (location) => !HIDDEN_CAMPUSES.includes(location.slug),
          ) || /* istanbul ignore next */ [];
        setLatLng(locationsJson);
        locationsJson = _.groupBy(
          locationsJson,
          (item) => split(item['@path'], '/')[1],
        );
        setLocations(locationsJson);
      } catch (error) {
        /* istanbul ignore next */
        Log.error(error);
      }
    }

    fetchLocations(); // NOSONAR
    // Note: Adding dependencies for hook-related state items so resize logic
    // for determining/setting image source is properly utilized and set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const handleFlyTo = (centerPoint, zoomValue, essential = true) => {
    if (window?.lifeChurchMap) {
      window.lifeChurchMap.flyTo({ centerPoint, essential, zoomValue }); // this animation is considered essential with respect to prefers-reduced-motion
    }
  };

  const mapLocations =
    !!locations &&
    flattenDeep(
      Object.keys(locations).map((state) => {
        return locations[state].map((location) => ({
          latitude: location.latitude ? Number(location.latitude) : false,
          longitude: location.longitude ? Number(location.longitude) : false,
          name: location.name,
          phase:
            !!location.phase && !!location.phase.field
              ? location.phase.field
              : 'Launched',
          slug: location.slug ? location.slug : location['@name'],
          state,
        }));
      }),
    );

  /**
   * Handler function to trigger analytics tracking.
   *
   * @param {Event} event - The Event object associated with the click.
   * @param {string} label - The label value used in the analytics properties data.
   */
  const callAnalytics = (event, label) => {
    /* istanbul ignore next */
    if (event?.target?.preventDefault && ENVIRONMENT.test) {
      event.preventDefault();
    }
    callSegmentTrack({
      event: EVENTS.buttonAction,
      properties: {
        action: ACTIONS.clicked,
        component: 'Locations Map',
        component_url: event?.currentTarget?.getAttribute('href'),
        label: label || event?.currentTarget?.textContent,
        logged_in: !!user,
        preferred_campus: null, // User preferred campus not presently available without specific call to API to get user-specific data (such as with Web Giving).
        referrer: document?.referrer || null,
        title: document?.title || '',
        url: window?.location?.href,
        user_id: user?.['https://www.life.church/rock_person_alias_id'],
      },
    });

    /* istanbul ignore next */
    callGtagEvent({
      parameters: {
        event_category: 'Map',
        event_label: label,
      },
      type: 'click',
    });
  };

  const sectionClass = `locations ${
    isMobile
      ? convertValueToClassName(sbOnMobile)
      : convertValueToClassName(sbOnTabletAndUp)
  }`;

  return (
    <section
      className={sectionClass}
      data-testid="lc-locations-listing"
      id={sectionId}
    >
      <div className="locations-map">
        {!!locations && (
          <Mapbox
            center={center}
            handleMap={() => {
              const offCenter =
                document.body.clientWidth >= BREAKPOINTS.large
                  ? [-95.4992354, 37.192453]
                  : [-99.4992354, 33.192453]; // starting position [lng, lat]

              handleFlyTo(zoom !== defaultZoomOut ? center : offCenter, zoom);
            }}
            locations={mapLocations}
            stateClicked={stateClicked}
            states={Object.keys(locations).map((location) =>
              location.replace(/-/g, ' '),
            )}
            zoom={zoom}
          />
        )}
      </div>
      <div className="locations-listing">
        <div className="container-desktop">
          <h4 className="location-heading justify-center">{headingText}</h4>
          <div className="location-heading justify-space-between">
            <a
              className="church-online"
              href={implementUtmParams(
                'https://live.life.church/',
                window?.location,
              )}
              onClick={(event) => callAnalytics(event)}
            >
              <svg
                enableBackground="new 0 0 25 25"
                height="25px"
                id="Layer_1"
                version="1.1"
                viewBox="0 0 25 25"
                width="25px"
                x="0px"
                xmlns="http://www.w3.org/2000/svg"
                y="0px"
              >
                <g>
                  <path
                    d="M23.5,9.8c-0.2-0.8-0.5-1.6-1-2.3c-1.3-2.5-3.2-4.1-5.4-4.9c-1.4-0.6-2.9-0.9-4.4-0.9   c-6.2,0-11.2,5-11.2,11.2S6.6,24,12.8,24c5.4,0,9.9-3.8,10.9-8.9C24.1,13.5,24,11.7,23.5,9.8z M5.6,6c0.1,0,0.3-0.1,0.5-0.3   c0.1,0.2,0.4,0,0.5-0.2c-0.2,0.1-0.2,0-0.4-0.1c0.1-0.1,0.2-0.2,0.4-0.3c0,0,0.2-0.1,0.2,0.2c0,0,0.1-0.1,0.2-0.2   C6.9,5.3,6.9,5.5,6.9,5.6c0-0.1,0.2,0,0.2-0.1C7.1,5.8,6.8,5.9,6.8,6.2C6.7,6.4,6.7,6.1,6.7,6c0,0-0.1,0-0.1,0   C6.4,6.3,6.1,6.1,5.9,6.1c0,0,0,0,0,0c-0.1,0-0.4,0.5-0.4,0.5c0,0-0.1-0.1-0.2-0.2C5.4,6.2,5.5,6.1,5.6,6z M7.7,10.3   c0,0.1-0.1,0.1-0.2,0.1c0.2,0.2-0.5,0.4-0.5,0.3c0-0.1,0,0,0,0c0-0.2,0.4-0.2,0.6-0.3c0,0,0,0-0.1,0c-0.1,0-0.2,0.1-0.4,0.1   c-0.1-0.2-0.2-0.2-0.2-0.4c-0.2,0.1-0.3,0.7-0.4,0.7c0.2-0.5,0.2-0.7,0-0.4C6.6,10.1,7,10,7,9.9C7,10,6.4,10,6.7,9.8   c-0.4,0-0.2,0.2-0.5,0.1c0,0,0.3-0.4,0.5-0.4c0.1-0.1,0.8,0.4,0.9,0.6c0,0-0.1,0-0.1,0.1C7.8,10.2,8.2,10.2,7.7,10.3z M3.2,12.6   C3.2,12.5,3.2,12.5,3.2,12.6c0.6,1.7,2.8,3.2,3.8,3.1c0.6,0.5-0.4,1.5-0.2,2c1.4,1,1.5,2.6,1.6,4c-3.2-1.6-5.4-5-5.4-8.8   c0-0.3,0-0.6,0-0.9C3,12.2,3.1,12.4,3.2,12.6z M9.3,22.1c0.1-0.1,0.2-0.3,0.2-0.2c0.8,0.1,0.4-0.1,0.3-0.5c0.1-0.1,0.5-0.1,0.7-0.1   c0,0,0,0,0,0c0,0,0.1,0,0.1,0c0,0,0,0,0,0c0.1,0,0.1,0,0.1-0.1c0.5-0.8,2.9-5.1,0.1-3.9c0,0,0-0.4,0-0.3c-0.1,0.1-0.2,0.2-0.3,0.3   c0.1,0.1-0.3-1.8-1.3-1.4c0,0,0-0.3,0.1-0.3c-0.2-0.2-0.7,0.2-1-0.2c-0.1,0-0.1,0.4-0.2,0.4c-0.1-0.2,0-0.2,0.1-0.4   c-0.2-0.2-0.7,0.5-0.7,0.6c-0.3-0.3-1-1.1-1.4-1.1C6.1,14.5,6,14.4,6,14.4c0,0,0,0,0,0c0-0.1,0.1-0.4,0.1-0.5c0-0.1-0.7,0.5-1,0.5   c0,0-0.4-1.5-0.2-1.3c0-0.4,1-0.4,1-0.3c0.1,0-0.1-0.2-0.1-0.2c0.6-0.6,0.8,0.6,1,0.7c0.3-0.8,0.5-2,0.9-2.3c0,0.1-0.2,0.2-0.1,0.3   c0.1,0,0,0,0.1,0.1c0.1-0.1,1.4-1.7,1.5-1.5C9.2,10,9.2,10,9.4,9.9C9.3,10,9.1,10.1,9,10.2c0.1,0.2,0.1,0,0.1,0.1   c0,0,0.5-0.4,0.5-0.4c-0.1,0-0.1,0,0-0.1C9.4,10,9.2,9.6,9.2,9.5c0.2,0,0.2-0.1,0.2-0.3C9,9.2,8.9,9.5,8.6,9.7   c0-0.6,1.5-0.8,1.8-1.3c-0.1-0.1-0.5,0-0.5,0c0.4,0-0.2-0.9-0.2-1.4c-0.2,0-0.1,0.5-0.4,0.5C9.3,7.3,9,7.3,9.2,7   c0.3-0.2-1.5,0.3-1,0.4C8.3,7.6,8.1,9,7.6,9c0,0-0.1-0.5,0-0.7C5.9,7.9,7.1,6.5,7.2,6.5c0.1-0.1,1.5-0.5,1.1-1   C8.1,5.6,8.2,5.4,8.2,5.4C7.8,5.5,8,5.9,7.6,6c-0.1-0.1-0.1-0.2,0-0.4C7.5,5.8,7.4,5.9,7.4,5.9c0.1-0.8,0-0.4,0.3-1.3   C7.5,4.6,7.3,4.8,7.1,5c0,0,0,0-0.1,0c0-0.1,0-0.1,0-0.2c0.2-0.1,0.4-0.3,0.6-0.4c0,0,0,0.1,0,0.1c0.1,0,1-0.2,1.1-0.4   C8.7,4,8.5,4,8.3,4C8.5,4,8.6,3.9,8.7,3.9c0,0,0,0,0,0c0.1,0,0.2-0.1,0.2-0.2c0.4-0.2,0.7-0.3,1.1-0.4c0,0,0.1,0,0.1,0   C10,3.6,9.7,3.5,9.6,3.5c0.1,0.5,1.2-0.2,1.3,1.1c0,0,0.4-0.1,0.5,0c-0.1,0.1-0.1,0.1,0,0.1c0,0.1,0,0.1,0,0.2c-0.1,0-0.3,0-0.4,0   c0.1,0,0.1,0.1,0.2,0.1c0,0-0.1,0-0.1,0c-0.3-0.1,0,0.3-0.1,0.3c0,0.2,0.3-0.1,0.3-0.2c0.2,0.1,0.1,0.1-0.1,0.5   c0.2-0.1,0.2-0.1,0.2,0c-0.1,0-0.1,0-0.2,0c0,0.1,0,0.1-0.1,0.2c0.5-0.3,0,0,0.3-0.1c0-0.1,0-0.1,0.1-0.1c-0.4,0.5-0.4,0.4,0,0.6   C10.8,6.5,11.9,7,12,7C12,7,12,7,12.1,7c-0.9-0.7,2.1-1.9,2.3-2.3c-0.2-0.1-0.3-0.1-0.5,0c0.2-0.2,0.2,0-0.2-0.1   c0.1-0.1,0.2-0.1,0.3-0.1c0-0.2-0.3,0-0.3-0.1c0.1,0,0.1,0,0-0.1c0.3,0,0.4,0.3,0.7,0.3c0.2-0.3-0.6-0.5-0.6-0.6c0.1,0,0.1,0,0-0.1   c0,0,0.5-0.1,0.6-0.1c0,0,0,0-0.1,0c-0.1,0-0.1-0.1,0-0.2c0.1,0.1,0.2,0.1,0.3,0c0,0-0.3-0.2-0.3-0.2c0-0.1,0.2,0,0.2,0   c0.2-0.4-0.1-0.2-0.2-0.2c0-0.2,0.1-0.1,0.3-0.1c0,0,0-0.1,0-0.1c1.6,0.3,3,1,4.2,1.9c-0.3,0.3-0.6,0.6-0.8,1.2   c-0.5-0.1-0.7,0.5-0.3,0.6c-0.4,0.4,0,0.6,0.5,0.3c0,0.2,0.1,0.4,0.2,0.6c0.1,0.1,0.1,0.1,0.1,0.2C19.1,8,18.8,6.6,19,6   c0-0.2,0.1-0.4,0.3-0.5c0.5,0.5,1,1,1.4,1.5c-0.2,0-0.4,0-0.7,0c-0.3,0.1-0.6,0.6-0.6,0.4c-0.9,0.3-0.7,0.7-1,0.4c0,0,0,0-0.1,0   c0,0,0-0.1,0-0.2c-0.1-0.1-0.1-0.2-0.3-0.3c0,0-0.1,0.1-0.2,0.2c0,0,0,0,0,0c0,0,0,0,0,0c0,0.1-0.2,0.5,0.1,0.6   c-0.5,0-1.3,0.8-1.6,0.9c0,0,0,0.1,0,0.1c1.1,0-0.9,1.1-0.4,1.9c0,0,2.1-1.3,2.8-1c-0.2-0.1-0.3-0.3-0.3-0.5   c0.5,0.1,0.9,1.1,1.2,1.5c0.2-0.1,0.3-0.3,0.4-0.1c0-0.1-0.5-0.3-0.2-0.5c0.2,0.2,0.3-0.3,0.4,0.1c0.1-0.1,0.2-0.1,0.3-0.2   c-0.3-0.1,0-0.8,0.2-0.9C21,9.5,21,10,21.4,9.9c0-0.1-0.3,0-0.3-0.2c0,0,0.5-0.1,0.6-0.1c-0.1,0.1-0.3,0.2-0.3,0.3   c0.1,0.1,0.5,0.3,0.6,0.5c-0.3-0.1-3.6,1.4-0.6,0.9c0.3,0.3-2.9,2.3-3-0.1c-0.6-0.1-4.7,1.2-3.5,2.7c-1.1,2.4,1.7,1.8,2.1,2.3   c0.4-0.2,0.7,0,0.9,0.3c0,0,0,0.1,0,0c0.1,0.3,0.2,0.8,0.2,1.1c0.9,0.4-0.5,1.8-0.1,2.3c-0.1,0.2,0,0.9,0,1.1c0,0,0,0-0.1,0.1   c0,0,0,0.1,0,0.1c0,0,0,0,0,0c-1.5,0.9-3.2,1.5-5.1,1.5C11.5,22.7,10.4,22.5,9.3,22.1z"
                    fill="#77CCE7"
                  />
                  <path
                    d="M9.7,5.6C9.6,5.4,9.5,4.5,9.1,4.9C8.9,4.8,9.6,4.2,8.7,4.6c0.1,0,0.2-0.1,0.1-0.3C8.3,4.2,8.1,5.1,7.9,5.1   C8,5,8.3,4.6,8.3,4.5c-0.8,0-0.7,1.3,0.4,0.6c0,0.2,0.2,0,0.2,0.4c0.7-0.3-0.5,1.2-0.5,1.1c0.3,0.1,0.3-0.2,0.3-0.2   c0,0.2,0.8,0.6,0.9,0.3c-0.1,0-0.3-0.1-0.3-0.2c0.1-0.1,0.4,0,0.4,0.1C9.9,6.4,9.4,6.1,9.5,6c0.2,0,0-0.1,0.1-0.1   c0,0.1,0.1,0.1,0,0C9.8,6,9.8,6,9.8,6.1c0.1,0,0,0,0.1,0c0.1-0.1,0.3-0.5,0.2-0.5C10.1,5.7,9.8,5.6,9.7,5.6z M8.4,4.9   c0.1,0,0.1-0.3,0.1-0.4c0,0,0.3,0.2,0.3,0.2C8.8,4.7,8.4,4.8,8.4,4.9z"
                    fill="#77CCE7"
                  />
                  <path
                    d="M14.8,6.2c0.1-0.1,0.3-0.3,0.5-0.3c0-0.4-1-0.3-1-0.3c0.1,0.2,0,0,0,0c0,0.1,0,0.1-0.1,0.2   c0,0.1,0.2-0.1,0.2,0c0,0.1-0.2,0-0.2,0.2C14.5,5.9,14.3,6.3,14.8,6.2z"
                    fill="#77CCE7"
                  />
                  <path
                    d="M16.6,8.1c0,0-0.2,0.4-0.3,0.5c0.1,0,0.2,0,0.3,0c-0.1,0.1-0.4,0.1-0.4,0.3c0.1,0,0.9-0.2,0.9-0.2   c-0.1-0.1-0.4-1.3-0.6-1.5c-0.1,0-0.3,0.2-0.2,0.3C16.1,7.5,16.4,8.1,16.6,8.1z"
                    fill="#77CCE7"
                  />
                  <path
                    d="M16.2,8c-0.6-0.1-0.2,0.5-0.5,0.6C15.8,9,16.2,8.1,16.2,8z"
                    fill="#77CCE7"
                  />
                  <path
                    d="M6.6,5.5C6.6,5.5,6.5,5.5,6.6,5.5C6.6,5.5,6.6,5.5,6.6,5.5z"
                    fill="#77CCE7"
                  />
                </g>
              </svg>
              <h6>{churchOnline}</h6>
            </a>
            <a
              className={`starts-soon ${!!isLive && 'active'}`}
              href={implementUtmParams(
                'https://live.life.church/',
                window?.location,
              )}
              onClick={(event) => callAnalytics(event)}
            >
              {isLive ? 'LIVE NOW' : 'STARTS SOON'}
            </a>
          </div>
          {!!locations &&
            Object.keys(locations)
              .sort() // NOSONAR
              .map((state) => {
                return (
                  <ListItem
                    key={state}
                    lc={locations[state]}
                    link={link}
                    onFlyTo={handleFlyTo}
                    onStateClicked={(stateClickedValue) =>
                      setStateClicked(stateClickedValue)
                    }
                    onZoomOut={setZoom}
                    override={override}
                    state={state}
                  />
                );
              })}
          <a
            className="location-link display-block p-normal text-center"
            data-testid="lc-location-listing-link-label"
            href={implementUtmParams(link.url, window?.location)}
            onClick={(event) => {
              callAnalytics(event);
            }}
            target={link.target}
          >
            {link.linklabel}
          </a>
        </div>
      </div>
    </section>
  );
};

export default LocationsListing;
